import type { Dispatch } from "react";
import { useRef } from "react";
import {
	type GradientState,
	type GradientStateAction,
	MIN_STOPS,
} from "../hooks/use-gradient-state";
import { usePointerDrag } from "../hooks/use-pointer-drag";
import { formatRgba, hexToRgba, isCssVar } from "../lib/rgba";
import type {
	GradientStop,
	LinearGradient,
	RadialGradient,
} from "../lib/types";
import { StopHandle } from "./stop-handle";

export type StopsTrackProps = {
	state: GradientState;
	dispatch: Dispatch<GradientStateAction>;
	canAddStop: boolean;
	canRemoveStop: boolean;
};

const CHECKER_STYLE: React.CSSProperties = {
	backgroundImage:
		"linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)",
	backgroundSize: "8px 8px",
	backgroundPosition: "0 0, 0 4px, 4px -4px, -4px 0",
};

/** Handle diameter in px */
const HANDLE_SIZE = 24;
/** Min distance from track edge to handle edge */
const EDGE_PADDING = 3;

const clampPercent = (n: number): number => Math.max(0, Math.min(100, n));

const buildNormalizedGradientCss = (stops: GradientStop[]): string => {
	const sorted = [...stops].sort((a, b) => a.position - b.position);
	const parts = sorted.map((s) => {
		if (isCssVar(s.color)) {
			return `${s.color} ${s.position}%`;
		}
		const { r, g, b } = hexToRgba(s.color);
		return `${formatRgba(r, g, b, s.opacity)} ${s.position}%`;
	});
	return `linear-gradient(90deg, ${parts.join(", ")})`;
};

export const StopsTrack = ({
	state,
	dispatch,
	canAddStop,
	canRemoveStop,
}: StopsTrackProps) => {
	const trackRef = useRef<HTMLDivElement>(null);
	const draggingIdRef = useRef<string | null>(null);

	const { onPointerDown: onTrackPointerDown } = usePointerDrag({
		onStart: (e) => {
			const rect = trackRef.current?.getBoundingClientRect();
			if (!rect) return false;
			const stopEl = (e.target as HTMLElement).closest<HTMLElement>(
				"[data-stop-id]",
			);
			if (stopEl) {
				draggingIdRef.current = stopEl.dataset.stopId ?? null;
				return true;
			}
			if (!canAddStop) return false;
			const percent = clampPercent(
				Math.round(((e.clientX - rect.left) / rect.width) * 100),
			);
			dispatch({ kind: "addStop", position: percent });
			draggingIdRef.current = null;
			return false;
		},
		onMove: (e) => {
			const id = draggingIdRef.current;
			const rect = trackRef.current?.getBoundingClientRect();
			if (!id || !rect) return;
			const percent = clampPercent(
				Math.round(((e.clientX - rect.left) / rect.width) * 100),
			);
			dispatch({ kind: "updateStopPosition", id, position: percent });
		},
		onEnd: () => {
			draggingIdRef.current = null;
		},
	});

	// StopsTrack is only rendered for linear/radial gradients (never mesh)
	const gradient = state.gradient as LinearGradient | RadialGradient;
	const canRemove = canRemoveStop && gradient.stops.length > MIN_STOPS;
	const sortedForRender = [...gradient.stops].sort(
		(a, b) => a.position - b.position,
	);

	return (
		<div
			ref={trackRef}
			data-testid="stops-track"
			onPointerDown={onTrackPointerDown}
			style={{ touchAction: "none" }}
			className="tw-relative tw-h-8 tw-w-full tw-rounded-full"
		>
			{/* Checkerboard underlay */}
			<div
				className="tw-absolute tw-inset-0 tw-overflow-hidden tw-rounded-full"
				style={CHECKER_STYLE}
			/>
			{/* Gradient layer */}
			<div
				data-testid="stops-track-gradient"
				className="tw-absolute tw-inset-0 tw-rounded-full tw-outline tw-outline-1 -tw-outline-offset-1 tw-outline-blue-base/10"
				style={{
					backgroundImage: buildNormalizedGradientCss(gradient.stops),
				}}
			/>
			{/* Stop handles — clamped so circles stay within 4px from edges */}
			{sortedForRender.map((stop, idx) => {
				const originalIndex = gradient.stops.findIndex((s) => s.id === stop.id);
				// Convert percentage position to a CSS left value that keeps the
				// 24px handle within EDGE_PADDING (4px) from each track edge.
				// At 0% → left: 4px, at 100% → right: 4px (i.e. left = calc(100% - 28px)).
				// Formula: left = EDGE_PADDING + (position/100) * (trackWidth - HANDLE_SIZE - 2*EDGE_PADDING)
				// In CSS: calc(4px + (position/100) * (100% - 32px))
				const leftCalc = `calc(${EDGE_PADDING}px + ${stop.position / 100} * (100% - ${HANDLE_SIZE + EDGE_PADDING * 2}px))`;

				return (
					<div
						key={stop.id}
						data-stop-id={stop.id}
						data-testid={`stops-track-handle-wrapper-${stop.id}`}
						style={{
							position: "absolute",
							top: "50%",
							left: leftCalc,
							transform: "translateY(-50%)",
							width: HANDLE_SIZE,
							height: HANDLE_SIZE,
						}}
					>
						<StopHandle
							stop={stop}
							index={originalIndex >= 0 ? originalIndex : idx}
							isSelected={stop.id === state.selectedStopId}
							canRemove={canRemove}
							onSelect={(id) => dispatch({ kind: "selectStop", id })}
							onPositionChange={(id, position) =>
								dispatch({
									kind: "updateStopPosition",
									id,
									position: clampPercent(position),
								})
							}
							onDelete={(id) => dispatch({ kind: "removeStop", id })}
						/>
					</div>
				);
			})}
		</div>
	);
};
